import i18n from 'i18next';

import {
  CashierConfigurationKeys,
  InputMetaData,
  PaymentMethodConfiguration,
} from '@cashier/cashier-model';

import { DEFAULT_MIN_AMOUNT, defaultMaxAmountsMap } from '../../models';
import { compareDecimals } from '../compareDecimals';

export const validateByCharactersType = (amount: string): boolean =>
  /^\d+(,\d{1,})?(\.\d{1,})?$/.test(amount);

export const validateByScale = (
  amount: string,
  params: InputMetaData,
): boolean => {
  const { scale } = params || {};
  if (typeof scale === 'undefined') return true;

  if (Math.sign(scale) === -1) {
    return Number(amount) % Math.pow(10, Math.abs(scale)) === 0;
  }

  if (Math.sign(scale) === 1) {
    const _amount = String(amount).split('.');

    if (_amount.length === 1) return true;

    return _amount[1].length <= Math.abs(scale);
  }

  if (Math.sign(scale) === 0) {
    return Math.floor(Number(amount)) === Number(amount);
  }
};

export const validateByLimits = (
  amount: string,
  min?: number,
  max?: number,
): boolean => {
  const preparedAmount = amount.replace(',', '.');
  if (min && max) {
    return (
      preparedAmount &&
      compareDecimals(preparedAmount, String(min)) >= 0 &&
      compareDecimals(preparedAmount, String(max)) <= 0
    );
  }

  if (min) {
    return preparedAmount && compareDecimals(preparedAmount, String(min)) >= 0;
  }

  if (max) {
    return preparedAmount && compareDecimals(preparedAmount, String(max)) <= 0;
  }

  // eslint-disable-next-line no-restricted-globals
  const params = new URLSearchParams(location.search);
  const activeType = params.get(CashierConfigurationKeys.PaymentType);

  return (
    compareDecimals(preparedAmount, String(DEFAULT_MIN_AMOUNT)) >= 0 &&
    compareDecimals(preparedAmount, String(defaultMaxAmountsMap[activeType])) <=
      0
  );
};

export const getAmountScaleValidationError = (
  method: PaymentMethodConfiguration,
  amount: string,
): string | null => {
  if (!amount) return null;

  const amountFieldConfig = method?.inputs.find(
    (input) => input.name === 'amount',
  );
  if (!amountFieldConfig) {
    return null;
  }

  const { scale } = amountFieldConfig.params;
  const absoluteScale = Math.abs(scale);

  if (typeof scale === 'undefined') return null;

  if (
    Math.sign(scale) === -1 &&
    Number(amount) % Math.pow(10, absoluteScale) !== 0
  ) {
    return i18n.t(
      'amountMultipleOfTensValidationFailed.message',
      'amountMultipleOfTensValidationFailed.message',
      { multipleTo: Math.pow(10, absoluteScale) },
    );
  }

  if (Math.sign(scale) === 1) {
    const amountParts = String(amount).split('.');
    if (amountParts.length !== 1 && amountParts[1].length > absoluteScale) {
      return i18n.t(
        'amountDecimalValidationFailed.message',
        'amountDecimalValidationFailed.message',
        { decimalCount: absoluteScale },
      );
    }
  }

  if (Math.sign(scale) === 0 && Math.floor(Number(amount)) !== Number(amount)) {
    return i18n.t('amountWholeNumberValidationFailed.message');
  }

  return null;
};

export const getAmountValidationErrors = ({
  method,
  amount,
}: {
  method: PaymentMethodConfiguration | null;
  amount: string | null;
}): string[] => {
  const errors: string[] = [];

  if (!amount) return errors;

  if (!validateByCharactersType(amount)) {
    errors.push(i18n.t('amountCharactersInvalid.message'));
    return errors;
  }

  const { ruleParams } = method || {};
  const { min, max } = ruleParams || {};

  if (!validateByLimits(amount, min, max)) {
    errors.push(i18n.t('minMaxValidationFailed.deposit.message'));
  }

  const amountScaleValidationError = getAmountScaleValidationError(
    method,
    amount,
  );

  if (amountScaleValidationError) {
    errors.push(amountScaleValidationError);
  }

  return errors;
};
